import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  Paper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import clickTracker from './utils/ClickTracker';

export default function PrismDocumentationPopover({ open, onClose, anchorEl }) {
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(false);
  const [soapNote, setSoapNote] = useState('');
  const [clickSummary, setClickSummary] = useState(null);
  const popoverRef = useRef(null);

  useEffect(() => {
    if (open) {
      // Get click summary
      const summary = clickTracker.generateSummary();
      setClickSummary(summary);
      
      // Auto-generate if we have clicks
      if (summary && summary.totalClicks > 0 && !generated) {
        handleGenerate();
      }
    }
  }, [open]);

  // Close when clicking outside
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, onClose]);

  const handleGenerate = () => {
    if (!clickSummary || clickSummary.totalClicks === 0) return;

    setGenerating(true);

    // Simulate AI generation
    setTimeout(() => {
      const note = generateSOAPFromClicks();
      setSoapNote(note);
      setGenerated(true);
      setGenerating(false);
    }, 1500);
  };

  const generateSOAPFromClicks = () => {
    if (!clickSummary || clickSummary.totalClicks === 0) {
      return 'No activity recorded. Interact with the EMR first.';
    }

    let soap = 'SUBJECTIVE:\n';
    soap += 'Chart review documented:\n';

    const sectionClicks = clickSummary.sections;
    const demographicsSection = sectionClicks.find(s => s.name === 'Demographics');
    if (demographicsSection && demographicsSection.count > 0) {
      soap += `- Reviewed patient demographics\n`;
    }

    soap += '\nOBJECTIVE:\n';
    soap += 'Documentation review completed:\n\n';

    sectionClicks.forEach(section => {
      if (section.count > 0) {
        soap += `${section.name} (${section.count} interactions):\n`;
        // Show recent actions
        const recentActions = section.actions.slice(-3);
        recentActions.forEach(action => {
          soap += `  • ${action.label}\n`;
        });
        soap += '\n';
      }
    });

    soap += 'ASSESSMENT:\n';
    soap += `- Chart interactions: ${clickSummary.totalClicks} recorded\n`;
    soap += `- Sections reviewed: ${sectionClicks.filter(s => s.count > 0).length}\n`;
    soap += '- Documentation complete based on activity\n';

    soap += '\nPLAN:\n';
    soap += '- Continue current management\n';
    soap += '- Follow up as scheduled\n';

    return soap;
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(soapNote);
      // Could add toast notification here
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (!open) return null;

  // Calculate position
  const getPosition = () => {
    if (!anchorEl) {
      return { top: '60px', left: '50%', transform: 'translateX(-50%)' };
    }

    const rect = anchorEl.getBoundingClientRect();
    const popupWidth = 400;
    const btnCenter = rect.left + (rect.width / 2);
    let left = btnCenter - (popupWidth / 2);

    // Keep within bounds
    if (left < 20) left = 20;
    if (left + popupWidth > window.innerWidth - 20) {
      left = window.innerWidth - popupWidth - 20;
    }

    return {
      top: `${rect.bottom + 12}px`,
      left: `${left}px`,
    };
  };

  const position = getPosition();

  return (
    <Paper
      ref={popoverRef}
      sx={{
        position: 'fixed',
        ...position,
        width: '400px',
        maxHeight: '70vh',
        zIndex: 9999,
        bgcolor: '#1e293b',
        color: '#e2e8f0',
        borderRadius: '12px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.2)',
        border: '1px solid rgba(148, 163, 184, 0.1)',
        animation: 'slideDown 0.3s ease-out',
        '@keyframes slideDown': {
          from: {
            transform: 'translateY(-20px)',
            opacity: 0,
          },
          to: {
            transform: 'translateY(0)',
            opacity: 1,
          },
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 1.5,
          borderBottom: '1px solid rgba(148, 163, 184, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: 'rgba(30, 41, 59, 0.95)',
        }}
      >
        <Box display="flex" alignItems="center" gap={1} sx={{ color: '#38bdf8', fontWeight: 600 }}>
          <AutoAwesomeIcon sx={{ fontSize: 16 }} />
          <Typography variant="body2" fontWeight={600}>
            AI Notes
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={onClose}
          sx={{
            color: '#94a3b8',
            '&:hover': { bgcolor: 'rgba(148, 163, 184, 0.1)' },
          }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      {/* Content */}
      <Box
        sx={{
          p: 2,
          flex: 1,
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(148, 163, 184, 0.1)',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(148, 163, 184, 0.3)',
            borderRadius: '4px',
            '&:hover': {
              background: 'rgba(148, 163, 184, 0.4)',
            },
          },
        }}
      >
        {generating ? (
          <Box display="flex" flexDirection="column" alignItems="center" gap={2} py={4}>
            <CircularProgress size={32} sx={{ color: '#38bdf8' }} />
            <Typography variant="body2" color="#cbd5e1">
              Generating notes from your activity...
            </Typography>
          </Box>
        ) : generated ? (
          <>
            {clickSummary && (
              <Alert
                severity="info"
                icon={<TouchAppIcon />}
                sx={{
                  mb: 2,
                  bgcolor: 'rgba(56, 189, 248, 0.1)',
                  border: '1px solid rgba(56, 189, 248, 0.2)',
                  '& .MuiAlert-icon': { color: '#38bdf8' },
                  '& .MuiAlert-message': { color: '#cbd5e1', fontSize: '12px' },
                }}
              >
                Generated from {clickSummary.totalClicks} interactions across{' '}
                {clickSummary.sections.filter(s => s.count > 0).length} sections
              </Alert>
            )}
            <Typography
              component="pre"
              sx={{
                fontSize: '13px',
                lineHeight: 1.6,
                color: '#cbd5e1',
                whiteSpace: 'pre-wrap',
                fontFamily: 'Menlo, Monaco, "Courier New", monospace',
                m: 0,
              }}
            >
              {soapNote}
            </Typography>
          </>
        ) : clickSummary && clickSummary.totalClicks === 0 ? (
          <Box display="flex" flexDirection="column" alignItems="center" gap={2} py={4}>
            <TouchAppIcon sx={{ fontSize: 48, color: '#475569' }} />
            <Typography variant="body2" color="#94a3b8" textAlign="center">
              No activity recorded yet.
              <br />
              Interact with the EMR first!
            </Typography>
          </Box>
        ) : null}
      </Box>

      {/* Footer */}
      {generated && (
        <Box
          sx={{
            p: 1.5,
            borderTop: '1px solid rgba(148, 163, 184, 0.1)',
            display: 'flex',
            justifyContent: 'flex-end',
            bgcolor: 'rgba(30, 41, 59, 0.95)',
          }}
        >
          <Button
            size="small"
            startIcon={<ContentCopyIcon />}
            onClick={handleCopy}
            sx={{
              bgcolor: '#38bdf8',
              color: '#0f172a',
              fontWeight: 600,
              fontSize: '13px',
              px: 1.5,
              py: 0.75,
              textTransform: 'none',
              '&:hover': {
                bgcolor: '#0ea5e9',
              },
            }}
          >
            Copy
          </Button>
        </Box>
      )}
    </Paper>
  );
}
